// src/pages/LandingPage.tsx - Valuate Landing Page
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Paper,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../app/providers/ThemeContext';
import ContactModal from '../components/ContactModal';
import {
  Assessment,
  Security,
  CheckCircle,
  ArrowForward,
  PlayArrow,
  Brightness4,
  Brightness7,
  Timeline,
  Insights,
  AutoGraph,
  Speed,
  DocumentScanner
} from '@mui/icons-material';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('General Inquiry - Valuate');

  const handleOpenContactModal = (subject: string) => {
    setContactSubject(subject);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  // Refined professional color palette
  const fintechColors = {
    primary: '#0F172A', // Slate 900 - Deep professional navy
    secondary: '#1E293B', // Slate 800
    accent: '#3B82F6', // Blue 500 - Professional blue
    accentLight: '#60A5FA', // Blue 400
    success: '#10B981', // Emerald 500
    successLight: '#34D399', // Emerald 400
    warning: '#F59E0B', // Amber 500
    purple: '#8B5CF6', // Violet 500
    purpleLight: '#A78BFA', // Violet 400
    chart1: '#3B82F6', // Blue 500
    chart2: '#8B5CF6', // Violet 500
    chart3: '#10B981', // Emerald 500
    textPrimary: '#F8FAFC', // Slate 50
    textSecondary: '#CBD5E1', // Slate 300
    border: '#334155', // Slate 700
  };

  const features = [
    {
      icon: AutoGraph,
      title: 'AI-Powered Analysis',
      description: 'Advanced multi-agent AI system using Google Gemini to analyze financial documents, market conditions, and investment potential with comprehensive valuation methodologies.',
      color: fintechColors.accent,
      gradient: `linear-gradient(135deg, ${fintechColors.accent}, ${fintechColors.chart1})`,
    },
    {
      icon: DocumentScanner,
      title: 'Smart Document Processing',
      description: 'Automatically digitize P&Ls, rent rolls, and STR reports. Maps every line item to USALI 12th Edition standards with clear AI vs. human-verified tagging.',
      color: fintechColors.success,
      gradient: `linear-gradient(135deg, ${fintechColors.success}, ${fintechColors.chart3})`,
    },
    {
      icon: Assessment,
      title: 'Multiple Valuation Methods',
      description: 'Eight distinct valuation approaches including DCF, cap rate, sales comparison, replacement cost, and revenue multiples with weighted consensus modeling.',
      color: fintechColors.purple,
      gradient: `linear-gradient(135deg, ${fintechColors.purple}, ${fintechColors.chart2})`,
    },
    {
      icon: Timeline,
      title: 'Market Intelligence',
      description: 'Competitive analysis using Google Places API to identify nearby hotels, assess market positioning, analyze demand drivers, and generate pricing strategies.',
      color: fintechColors.warning,
      gradient: `linear-gradient(135deg, ${fintechColors.warning}, #FF6B35)`,
    },
    {
      icon: Speed,
      title: 'Institutional Underwriting',
      description: 'Full capital stack modeling with debt sizing (DSCR/LTV/Yield), LP/GP waterfalls with multi-tier hurdles, and detailed 10-year pro forma projections.',
      color: fintechColors.chart1,
      gradient: `linear-gradient(135deg, ${fintechColors.chart1}, ${fintechColors.accent})`,
    },
    {
      icon: Insights,
      title: 'Risk & Sensitivity Analysis',
      description: 'Comprehensive risk assessment across market, financial, operational, and property-specific factors. What-if scenarios and stress testing for informed decisions.',
      color: fintechColors.chart2,
      gradient: `linear-gradient(135deg, ${fintechColors.chart2}, ${fintechColors.purple})`,
    },
  ];

  const capabilities = [
    'Instantly digitize P&Ls and map to USALI 12th Edition',
    'Run 8 simultaneous valuation methodologies',
    'Model LP/GP waterfalls and complex capital stacks',
    'Integrate STR data for live market benchmarking',
    'Manage deal pipeline from sourcing to closing',
    'Ask the AI Analyst complex "what-if" scenarios'
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Navigation Header */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={theme.palette.mode === 'dark' ? '/logo192-dark.png' : '/logo192.png'}
                alt="Valuate Hotels"
                style={{ height: 36, width: 36, marginRight: 10 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Valuate
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                onClick={toggleTheme}
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  borderRadius: 2,
                  borderColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.5) : theme.palette.primary.main,
                  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.dark,
                    backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${fintechColors.primary} 0%, ${fintechColors.secondary} 100%)`
            : `linear-gradient(135deg, ${fintechColors.primary} 0%, ${fintechColors.secondary} 50%, #0A1628 100%)`,
          color: 'white',
          py: { xs: 14, md: 20 },
          mt: 8,
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern - Grid overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(fintechColors.accent, 0.05)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(fintechColors.accent, 0.05)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            opacity: 0.4,
          }}
        />
        {/* Gradient overlays */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 30%, ${alpha(fintechColors.accent, 0.15)} 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, ${alpha(fintechColors.purple, 0.12)} 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, ${alpha(fintechColors.success, 0.08)} 0%, transparent 60%)
            `,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade appear={false} in timeout={1000}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={2}>
                  <Box>
                    <Chip
                      label="AI-POWERED HOTEL VALUATION PLATFORM"
                      sx={{
                        backgroundColor: alpha(fintechColors.accent, 0.1),
                        color: fintechColors.accentLight,
                        fontWeight: 600,
                        mb: 1.5,
                        border: `1px solid ${alpha(fintechColors.accent, 0.4)}`,
                        px: 2.5,
                        py: 0.5,
                        fontSize: '0.7rem',
                        letterSpacing: '1.2px',
                        textTransform: 'uppercase',
                      }}
                    />
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 800,
                        mb: 1.5,
                        fontSize: { xs: '2.5rem', md: '3.75rem' },
                        lineHeight: 1.15,
                        letterSpacing: '-0.025em',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        color: 'white',
                      }}
                    >
                      Intelligent Analysis for{' '}
                      <Box
                        component="span"
                        sx={{
                          background: `linear-gradient(120deg, ${fintechColors.accentLight} 0%, ${fintechColors.successLight} 100%)`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        Hotel Investment
                      </Box>
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: fintechColors.textSecondary,
                        fontWeight: 400,
                        lineHeight: 1.7,
                        maxWidth: 620,
                        mb: 2,
                        fontSize: { xs: '1.05rem', md: '1.15rem' },
                      }}
                    >
                      Advanced AI platform that automatically processes financial documents, maps to USALI 12th Edition, and generates institutional-grade investment models with 8-method valuations.
                    </Typography>

                    {/* Platform Highlights */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        gap: 2.5,
                        mb: 2,
                        p: { xs: 2.5, sm: 3.5 },
                        borderRadius: 3,
                        background: alpha('#000000', 0.4),
                        backdropFilter: 'blur(30px)',
                        border: `1px solid ${alpha(fintechColors.border, 0.5)}`,
                        boxShadow: `0 8px 32px ${alpha('#000000', 0.3)}`,
                      }}
                    >
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: fintechColors.accentLight, fontFamily: '"SF Mono", "Monaco", monospace' }}>
                          20+
                        </Typography>
                        <Typography variant="body2" sx={{ color: fintechColors.textSecondary, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600 }}>
                          Specialized AI Agents
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: fintechColors.successLight, fontFamily: '"SF Mono", "Monaco", monospace' }}>
                          8
                        </Typography>
                        <Typography variant="body2" sx={{ color: fintechColors.textSecondary, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600 }}>
                          Valuation Methods
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: fintechColors.warning, fontFamily: '"SF Mono", "Monaco", monospace' }}>
                          50%
                        </Typography>
                        <Typography variant="body2" sx={{ color: fintechColors.textSecondary, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600 }}>
                          Faster Analysis
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/signup')}
                      endIcon={<ArrowForward />}
                      sx={{
                        background: fintechColors.accent,
                        color: '#ffffff',
                        fontWeight: 600,
                        px: 2,
                        py: 1.75,
                        fontSize: '0.95rem',
                        borderRadius: 2.5,
                        boxShadow: `0 4px 20px ${alpha(fintechColors.accent, 0.3)}`,
                        textTransform: 'none',
                        letterSpacing: '0.3px',
                        minHeight: 54,
                        '&:hover': {
                          background: fintechColors.accentLight,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 30px ${alpha(fintechColors.accent, 0.4)}`,
                        },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiButton-endIcon': {
                          transition: 'transform 0.25s ease',
                        },
                        '&:hover .MuiButton-endIcon': {
                          transform: 'translateX(3px)',
                        },
                      }}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={() => handleOpenContactModal('Demo Request - Valuate')}
                      sx={{
                        color: fintechColors.textPrimary,
                        borderColor: alpha(fintechColors.border, 0.6),
                        fontWeight: 600,
                        px: 2,
                        py: 1.75,
                        fontSize: '0.95rem',
                        borderRadius: 2.5,
                        textTransform: 'none',
                        letterSpacing: '0.3px',
                        borderWidth: '1.5px',
                        backdropFilter: 'blur(10px)',
                        background: alpha('#ffffff', 0.05),
                        '&:hover': {
                          borderColor: fintechColors.accentLight,
                          backgroundColor: alpha(fintechColors.accent, 0.1),
                          borderWidth: '1.5px',
                        },
                        transition: 'all 0.25s ease',
                      }}
                    >
                      Book a Demo
                    </Button>
                  </Stack>

                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                {/* Enhanced Dashboard Preview */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 350, md: 550 },
                    background: alpha('#000000', 0.5),
                    borderRadius: 3,
                    border: `1px solid ${alpha(fintechColors.accent, 0.3)}`,
                    backdropFilter: 'blur(20px)',
                    cursor: 'pointer',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    boxShadow: `0 20px 60px ${alpha(fintechColors.accent, 0.2)}, inset 0 1px 0 ${alpha(fintechColors.accent, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 30px 80px ${alpha(fintechColors.accent, 0.3)}, inset 0 1px 0 ${alpha(fintechColors.accent, 0.2)}`,
                      border: `1px solid ${alpha(fintechColors.accent, 0.5)}`,
                      '& .dashboard-elements': {
                        transform: 'scale(1.02)',
                      },
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        linear-gradient(${alpha(fintechColors.accent, 0.1)} 1px, transparent 1px),
                        linear-gradient(90deg, ${alpha(fintechColors.accent, 0.1)} 1px, transparent 1px)
                      `,
                      backgroundSize: '20px 20px',
                      opacity: 0.3,
                      pointerEvents: 'none',
                    }
                  }}
                  onClick={() => navigate('/signup')}
                >
                  <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2
                  }}>
                    {/* Mock Terminal Interface */}
                    <Box
                      className="dashboard-elements"
                      sx={{
                        transition: 'transform 0.3s ease',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Terminal Header */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5,
                        pb: 2,
                        borderBottom: `1px solid ${alpha(fintechColors.accent, 0.2)}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: fintechColors.success }} />
                        </Box>
                        <Typography variant="caption" sx={{
                          color: fintechColors.accent,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '1px',
                          fontFamily: 'monospace'
                        }}>
                          VALUATE ANALYTICS
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            className="pulse-dot"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: fintechColors.success,
                              mr: 0.5,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.4 },
                                '100%': { opacity: 1 },
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{
                            color: fintechColors.success,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            fontFamily: 'monospace'
                          }}>
                            LIVE
                          </Typography>
                        </Box>
                      </Box>

                      {/* Key Metrics Cards */}
                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        <Grid item xs={6}>
                          <Box sx={{
                            background: alpha(fintechColors.primary, 0.6),
                            borderRadius: 1,
                            p: 2,
                            border: `1px solid ${alpha(fintechColors.accent, 0.3)}`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '2px',
                              background: `linear-gradient(90deg, ${fintechColors.accent}, transparent)`,
                            }
                          }}>
                            <Typography variant="caption" sx={{
                              color: alpha(fintechColors.accent, 0.9),
                              fontSize: '0.65rem',
                              display: 'block',
                              fontFamily: 'monospace',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5
                            }}>
                              EST. VALUE
                            </Typography>
                            <Typography variant="h6" sx={{
                              color: fintechColors.accent,
                              fontWeight: 700,
                              fontSize: '1.4rem',
                              fontFamily: 'monospace'
                            }}>
                              $42.7M
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: fintechColors.success,
                              fontSize: '0.65rem',
                              fontFamily: 'monospace'
                            }}>
                              ↑ 12.3%
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{
                            background: alpha(fintechColors.primary, 0.6),
                            borderRadius: 1,
                            p: 2,
                            border: `1px solid ${alpha(fintechColors.success, 0.3)}`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '2px',
                              background: `linear-gradient(90deg, ${fintechColors.success}, transparent)`,
                            }
                          }}>
                            <Typography variant="caption" sx={{
                              color: alpha(fintechColors.success, 0.9),
                              fontSize: '0.65rem',
                              display: 'block',
                              fontFamily: 'monospace',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5
                            }}>
                              CAP RATE
                            </Typography>
                            <Typography variant="h6" sx={{
                              color: fintechColors.success,
                              fontWeight: 700,
                              fontSize: '1.4rem',
                              fontFamily: 'monospace'
                            }}>
                              8.2%
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: alpha('#ffffff', 0.6),
                              fontSize: '0.65rem',
                              fontFamily: 'monospace'
                            }}>
                              Market avg
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Chart visualization */}
                      <Box sx={{
                        background: alpha(fintechColors.primary, 0.6),
                        borderRadius: 1,
                        p: 2,
                        border: `1px solid ${alpha(fintechColors.purple, 0.3)}`,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: `linear-gradient(90deg, ${fintechColors.purple}, transparent)`,
                        }
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="caption" sx={{
                            color: alpha(fintechColors.purple, 0.9),
                            fontSize: '0.65rem',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            VALUATION METHODS
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: fintechColors.success,
                            fontSize: '0.65rem',
                            fontFamily: 'monospace'
                          }}>
                            8 METHODS
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'end',
                          justifyContent: 'space-between',
                          height: 100,
                          gap: 0.5,
                          flex: 1
                        }}>
                          {[75, 82, 68, 85, 78, 80, 72, 88].map((height, index) => (
                            <Box key={index} sx={{
                              flex: 1,
                              height: `${height}%`,
                              background: `linear-gradient(to top, ${alpha(fintechColors.chart1, 0.6)}, ${alpha(fintechColors.accent, 0.8)})`,
                              borderRadius: '2px 2px 0 0',
                              transition: 'all 0.3s ease',
                              border: `1px solid ${alpha(fintechColors.accent, 0.5)}`,
                              borderBottom: 'none',
                              '&:hover': {
                                transform: 'scaleY(1.05)',
                                opacity: 1
                              }
                            }} />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" sx={{
                            color: alpha('#ffffff', 0.5),
                            fontSize: '0.55rem',
                            fontFamily: 'monospace'
                          }}>
                            DCF
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: alpha('#ffffff', 0.5),
                            fontSize: '0.55rem',
                            fontFamily: 'monospace'
                          }}>
                            COMP
                          </Typography>
                        </Box>
                      </Box>

                      {/* CTA */}
                      <Box sx={{
                        textAlign: 'center',
                        mt: 1.5,
                        pt: 2,
                        borderTop: `1px solid ${alpha(fintechColors.accent, 0.2)}`
                      }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: fintechColors.accent,
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                          }}
                        >
                          → START ANALYZING
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha('#ffffff', 0.6),
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            fontFamily: 'monospace'
                          }}
                        >
                          Click to get started
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Chip
            label="POWERED BY ADVANCED AI"
            sx={{
              backgroundColor: alpha(fintechColors.success, 0.1),
              color: fintechColors.successLight,
              fontWeight: 600,
              mb: 1.5,
              border: `1px solid ${alpha(fintechColors.success, 0.3)}`,
              px: 2.5,
              py: 0.5,
              fontSize: '0.7rem',
              letterSpacing: '1.2px',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 1.5,
              color: theme.palette.text.primary,
              fontSize: { xs: '2rem', md: '3rem' },
              letterSpacing: '-0.025em',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            Platform Capabilities
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', opacity: 0.9 }}>
            Multi-agent AI system that orchestrates specialized agents to deliver comprehensive hotel investment analysis.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Fade appear={false} in timeout={800 + index * 200}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    border: `1px solid ${alpha(feature.color, 0.2)}`,
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.6)
                      : alpha(theme.palette.background.paper, 0.9),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 20px 50px ${alpha(feature.color, 0.25)}`,
                      borderColor: alpha(feature.color, 0.5),
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.8)
                        : alpha(theme.palette.background.paper, 1),
                      '& .feature-icon': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 10px 30px ${alpha(feature.color, 0.4)}`,
                      },
                      '&::before': {
                        opacity: 1,
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: feature.gradient,
                      opacity: 0.7,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 8px 24px ${alpha(feature.color, 0.3)}`,
                        border: `1px solid ${alpha(feature.color, 0.3)}`,
                      }}
                    >
                      <feature.icon sx={{
                        fontSize: 28,
                        color: '#ffffff',
                      }} />
                    </Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      color: theme.palette.text.primary,
                      fontSize: '1.05rem',
                      letterSpacing: '-0.01em'
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      lineHeight: 1.65,
                      fontSize: '0.875rem',
                      opacity: 0.9
                    }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{
        background: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.5)
          : alpha(theme.palette.background.paper, 0.5),
        py: { xs: 10, md: 14 }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 1.5,
                color: theme.palette.text.primary,
                fontSize: { xs: '2rem', md: '3rem' },
                letterSpacing: '-0.025em',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              Simple, Powerful Workflow
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7, opacity: 0.9 }}>
              Upload your documents, let AI do the work, and get comprehensive analysis in minutes.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${fintechColors.accent}, ${fintechColors.chart1})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1.5,
                  boxShadow: `0 8px 32px ${alpha(fintechColors.accent, 0.3)}`,
                }}>
                  <DocumentScanner sx={{ fontSize: 40, color: '#ffffff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  1. Upload Documents
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Drop your financial statements, P&L reports, STR data, or property documents. Supports Excel, PDF, CSV, and images.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${fintechColors.success}, ${fintechColors.chart3})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1.5,
                  boxShadow: `0 8px 32px ${alpha(fintechColors.success, 0.3)}`,
                }}>
                  <AutoGraph sx={{ fontSize: 40, color: '#ffffff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  2. AI Analysis
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Our multi-agent AI system processes your data, runs market analysis, calculates valuations, and assesses risks automatically.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${fintechColors.purple}, ${fintechColors.chart2})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1.5,
                  boxShadow: `0 8px 32px ${alpha(fintechColors.purple, 0.3)}`,
                }}>
                  <Insights sx={{ fontSize: 40, color: '#ffffff' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  3. Get Insights
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Review comprehensive analysis with investment scoring, risk assessment, and actionable recommendations for data-driven decisions.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 1.5,
                color: theme.palette.text.primary,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.025em',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              Built for Hotel Investors
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7, fontSize: '1.05rem', opacity: 0.9 }}>
              Whether you're analyzing a single property or managing a portfolio, Valuate streamlines your workflow.
            </Typography>

            <Stack spacing={2.5}>
              {capabilities.map((capability, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: 2,
                  borderRadius: 1.5,
                  background: alpha(fintechColors.success, 0.05),
                  border: `1px solid ${alpha(fintechColors.success, 0.15)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha(fintechColors.success, 0.08),
                    borderColor: alpha(fintechColors.success, 0.25),
                    transform: 'translateX(4px)',
                  }
                }}>
                  <CheckCircle sx={{
                    color: fintechColors.success,
                    mr: 2,
                    mt: 0.2,
                    fontSize: 20
                  }} />
                  <Typography variant="body1" sx={{
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    lineHeight: 1.6
                  }}>
                    {capability}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${alpha(fintechColors.secondary, 0.8)}, ${alpha(fintechColors.primary, 0.9)})`
                  : `linear-gradient(135deg, ${alpha(fintechColors.accent, 0.05)}, ${alpha(fintechColors.accent, 0.08)})`,
                border: `1px solid ${alpha(fintechColors.border, 0.3)}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 20px 60px ${alpha('#000000', 0.4)}`
                  : `0 20px 60px ${alpha(fintechColors.accent, 0.08)}`,
              }}
            >
              <Typography variant="h4" sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: 'center',
                fontSize: '1.75rem',
                letterSpacing: '-0.02em',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}>
                Get Started Today
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.7,
                fontSize: '1rem',
                opacity: 0.9
              }}>
                Start analyzing hotel investments with AI-powered insights in minutes.
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/signup')}
                  endIcon={<ArrowForward />}
                  sx={{
                    py: 2,
                    borderRadius: 2.5,
                    background: fintechColors.accent,
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    boxShadow: `0 4px 20px ${alpha(fintechColors.accent, 0.3)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 30px ${alpha(fintechColors.accent, 0.4)}`,
                      background: fintechColors.accentLight,
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => handleOpenContactModal('Demo Request - Valuate')}
                  sx={{
                    py: 2,
                    borderRadius: 2.5,
                    borderColor: alpha(fintechColors.border, 0.4),
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    borderWidth: '1.5px',
                    '&:hover': {
                      borderColor: fintechColors.accent,
                      background: alpha(fintechColors.accent, 0.05),
                      borderWidth: '1.5px',
                    },
                  }}
                >
                  Request Demo
                </Button>
              </Stack>
              <Divider sx={{ my: 1.5, borderColor: alpha(fintechColors.border, 0.2) }} />
              <Typography variant="caption" color="text.secondary" sx={{
                textAlign: 'center',
                display: 'block',
                fontSize: '0.7rem',
                letterSpacing: '0.8px',
                opacity: 0.7,
                fontWeight: 600
              }}>
                AI-POWERED • SECURE • RELIABLE
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          background: theme.palette.mode === 'dark'
            ? fintechColors.primary
            : fintechColors.primary,
          color: 'white',
          py: 10,
          borderTop: `1px solid ${alpha(fintechColors.border, 0.3)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} sx={{ mb: 5 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2.5,
                  color: fintechColors.textPrimary,
                  letterSpacing: '-0.02em',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                Valuate
              </Typography>
              <Typography variant="body2" sx={{
                color: fintechColors.textSecondary,
                mb: 1.5,
                lineHeight: 1.7,
                fontSize: '0.95rem',
                opacity: 0.9
              }}>
                AI-powered hotel valuation platform for smart investment decisions.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Security sx={{
                  fontSize: 18,
                  color: fintechColors.accentLight
                }} />
                <Typography variant="body2" sx={{
                  color: fintechColors.textSecondary,
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}>
                  Secure • Reliable • Fast
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Company
                  </Typography>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      onClick={() => navigate('/about')}
                      sx={{
                        color: alpha('#ffffff', 0.6),
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      About Us
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => handleOpenContactModal('General Inquiry - Valuate')}
                      sx={{
                        color: alpha('#ffffff', 0.6),
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Contact Us
                    </Typography>
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Legal
                  </Typography>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      onClick={() => navigate('/privacy')}
                      sx={{
                        color: alpha('#ffffff', 0.6),
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Privacy Policy
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => navigate('/terms')}
                      sx={{
                        color: alpha('#ffffff', 0.6),
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Terms of Service
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{
            textAlign: 'center',
            pt: 2,
            borderTop: `1px solid ${alpha(fintechColors.border, 0.3)}`
          }}>
            <Typography variant="body2" sx={{
              color: fintechColors.textSecondary,
              fontSize: '0.85rem',
              letterSpacing: '0.2px',
              opacity: 0.7
            }}>
              © 2026 Valuate. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{
              color: fintechColors.textSecondary,
              fontSize: '0.75rem',
              mt: 1.5,
              display: 'block',
              opacity: 0.6
            }}>
              Not financial or investment advice. Conduct your own due diligence.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Modal */}
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        subject={contactSubject}
      />
    </Box>
  );
};

export default LandingPage;
